/**
 * ForgotPassword — two-phase Cognito password recovery.
 *
 * Phase 1: user enters email → we call forgotPassword(), Cognito emails
 *          a one-time verification code to the account's verified email.
 * Phase 2: user enters code + new password → confirmPassword() resets
 *          the password and bounces them back to /login.
 *
 * We intentionally don't auto-sign-in after reset — an explicit login
 * step after a password change is the safer default.
 */
import { FormEvent, useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';

type Phase = 'request' | 'confirm';

export default function ForgotPassword() {
  const { user, loading, requestPasswordReset, confirmPasswordReset } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return <p style={{ padding: 'var(--sp-6)' }}>Checking session…</p>;
  }
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  async function onRequest(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setPhase('confirm');
      setInfo('Check your email for a verification code.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start password reset');
    } finally {
      setSubmitting(false);
    }
  }

  async function onConfirm(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(email, code.trim(), newPassword);
      navigate('/login', {
        replace: true,
        state: { notice: 'Password reset — sign in with your new password.' },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset password');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        maxWidth: 420,
        margin: '10vh auto',
        padding: 'var(--sp-8)',
        background: 'var(--bg-raised)',
        border: '1px solid var(--border)',
        borderRadius: 8,
      }}
    >
      <h1 style={{ textAlign: 'center' }}>Reset password</h1>

      {phase === 'request' ? (
        <form onSubmit={onRequest} style={{ display: 'grid', gap: 'var(--sp-4)' }}>
          <p style={{ margin: 0, color: 'var(--fg-muted)' }}>
            Enter the email on your account and we'll send you a code to reset your password.
          </p>
          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span>Email</span>
            <input
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={inputStyle}
            />
          </label>
          {error && <p style={{ color: 'var(--danger)', margin: 0 }}>{error}</p>}
          <button type="submit" disabled={submitting} style={primaryButton(submitting)}>
            {submitting ? 'Sending…' : 'Send code'}
          </button>
        </form>
      ) : (
        <form onSubmit={onConfirm} style={{ display: 'grid', gap: 'var(--sp-4)' }}>
          <p style={{ margin: 0, color: 'var(--fg-muted)' }}>
            Enter the code we sent to <strong>{email}</strong> and choose a new password.
          </p>
          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span>Verification code</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              style={inputStyle}
            />
          </label>
          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span>New password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              style={inputStyle}
            />
          </label>
          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span>Confirm new password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              required
              minLength={8}
              style={inputStyle}
            />
          </label>
          {info && <p style={{ color: 'var(--active)', margin: 0 }}>{info}</p>}
          {error && <p style={{ color: 'var(--danger)', margin: 0 }}>{error}</p>}
          <button type="submit" disabled={submitting} style={primaryButton(submitting)}>
            {submitting ? 'Resetting…' : 'Reset password'}
          </button>
          <button
            type="button"
            onClick={() => {
              setPhase('request');
              setCode('');
              setNewPassword('');
              setConfirmNewPassword('');
              setError(null);
              setInfo(null);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--fg-muted)',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: 14,
            }}
          >
            Didn't get a code? Send another
          </button>
        </form>
      )}

      <p style={{ textAlign: 'center', marginTop: 'var(--sp-6)' }}>
        <Link to="/login" style={{ color: 'var(--sheet-a)' }}>
          Back to sign in
        </Link>
      </p>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'var(--bg-base)',
  color: 'var(--fg-strong)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  fontFamily: 'var(--font-body)',
  fontSize: 16,
};

function primaryButton(busy: boolean): React.CSSProperties {
  return {
    padding: 'var(--sp-3) var(--sp-4)',
    background: 'var(--sheet-a)',
    color: 'var(--bg-deep)',
    border: 'none',
    borderRadius: 4,
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    cursor: busy ? 'wait' : 'pointer',
  };
}
